// src/pages/HomePage.js
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ONI_FORMS, PERKS_LIST } from "../data/demons";
import "./HomePage.css";

export default function HomePage() {
  const revealRefs = useRef([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    revealRefs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const addReveal = (el) => { if (el) revealRefs.current.push(el); };

  return (
    <div className="homepage">
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-bg-glow" />
        <div className="hero-kanji-bg" aria-hidden="true"><span>鬼</span></div>

        <div className="hero-pillars" aria-hidden="true">
          <span className="pillar">卓球鬼道場激怒破壊</span>
          <span className="pillar">悪魔的技術混沌の力</span>
          <span className="pillar">鬼の覚醒燃える魂戦</span>
          <span className="pillar">怒り破壊卓球鬼道場</span>
        </div>

        <div className="hero-content">
          <p className="hero-eyebrow">卓球鬼 · LIFE WEAR · DEPUIS L'ENFER DU PING</p>
          <h1 className="hero-title">
            <span>TAKKYU</span>
            <span>ONI</span>
          </h1>
          <p className="hero-sub">AWAKEN YOUR DEMON</p>
          <p className="hero-tagline">
            Quelque part entre le filet qui te vole le point et le mec qui fait le smasher
            à l'entraînement, un démon dort en toi. Il est temps de le nommer.
          </p>
          <div className="hero-cta">
            <Link to="/awaken" className="btn btn-primary">RÉVEILLER MON DÉMON →</Link>
            <Link to="/lore" className="btn btn-ghost">DÉCOUVRIR LE LORE</Link>
          </div>
        </div>

        <div className="hero-scroll-hint">
          <span>SCROLL</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* ── LORE TEASER ── */}
      <section className="section">
        <div ref={addReveal} className="reveal lore-teaser">
          <div className="lore-teaser-text">
            <span className="section-eyebrow">伝説 · La Légende</span>
            <h2 className="section-title">L'Origine<br/>des Oni</h2>
            <div className="divider" />
            <p>Il est dit que le premier coup de raquette frappé dans la <span style={{color:"var(--blood)"}}>rage pure</span> ouvrit une porte. Pas une porte vers la victoire — une porte vers l'autre côté.</p>
            <p style={{marginTop:"1rem"}}>De l'autre côté vivent les <span style={{color:"var(--blood)"}}>Takkyu Oni</span> : démons nés de chaque mauvaise excuse, de chaque service illégal assumé, de chaque rouleau qui passe sur le filet en finale.</p>
            <Link to="/lore" className="btn btn-ghost" style={{marginTop:"2rem"}}>LIRE LE LORE COMPLET →</Link>
          </div>
          <div className="lore-teaser-scroll">
            <div className="lore-scroll-card">
              <span className="scroll-mark">鬼</span>
              <p className="scroll-jp">「卓球の鬼は怒りから生まれる。<br/>しかし、その怒りこそが力だ。」</p>
              <p className="scroll-fr">"Le démon du ping naît de la colère.<br/>Mais cette colère est sa force."</p>
              <cite>— AINO, Démon en Chef</cite>
            </div>
          </div>
        </div>
      </section>

      {/* ── ONI FORMS PREVIEW ── */}
      <section className="oni-preview-section">
        <div className="section">
          <div ref={addReveal} className="reveal" style={{textAlign:"center", marginBottom:"3rem"}}>
            <span className="section-eyebrow">原型 · Les Formes</span>
            <h2 className="section-title">Les Oni</h2>
            <div className="divider divider-center" />
          </div>

          <div className="oni-grid">
            {ONI_FORMS.map((oni, i) => (
              <div
                key={oni.id}
                ref={addReveal}
                className="reveal oni-card"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="oni-emoji">{oni.emoji}</div>
                <div className="oni-jp">{oni.jp}</div>
                <div className="oni-name">{oni.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PERKS TICKER ── */}
      <div className="perks-ticker" aria-hidden="true">
        <div className="ticker-track">
          {[...PERKS_LIST, ...PERKS_LIST].map((p, i) => (
            <span key={i} className="ticker-item">⚡ {p.label}</span>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="section" style={{textAlign:"center"}}>
          <div ref={addReveal} className="reveal">
            <span className="section-eyebrow">覚醒 · Le Réveil</span>
            <h2 className="section-title">Prêt à affronter<br/>ton démon ?</h2>
            <div className="divider divider-center" />
            <p style={{color:"var(--ash)", maxWidth:"500px", margin:"0 auto 2.5rem", lineHeight:2}}>
              5 questions. Tes perks. Ton archétype Oni.
              Un profil démoniaque qui évolue avec toi.
            </p>
            <Link to="/awaken" className="btn btn-primary" style={{fontSize:"1.2rem", padding:"16px 40px"}}>
              COMMENCER LE QUIZ →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
